import React, { useState, useEffect, useLayoutEffect, useMemo, useCallback, useRef, createContext, useContext } from 'react';
import { useNavigate, useParams, useSearchParams, useLocation } from 'react-router-dom';
import { useNavigateBack, getLocationReturnPath } from '../../hooks/useNavigateBack';
import { useSWRConfig } from 'swr';
import { Button } from '../ui';
import FormLayoutRenderer from './FormLayoutRenderer';
import { FormBackButton } from './FormBackButton';
import { renderField, FieldConfig } from './fieldRenderers';
import { useTheme } from '../../contexts/ThemeContext';
import { useNotification } from '../../contexts/NotificationContext';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import { validateField as validateFieldUtil } from '../../utils/validation';
import ValidationMessage from '../Form/ValidationMessage';
import DynamicFieldRenderer from '../Form/DynamicFieldRenderer';
import { getEntityTypeFromRoute, getEntityTypeFromTitle } from '../../utils/entityTypeMapper';
import { setCustomFieldValue } from '../../modules/admin/custom-fields/useData';
import { FileAttachmentsFormBlock } from '../attachments/FileAttachmentsFormBlock';
import type { FormLayoutVariant } from './FormLayoutRenderer';

// Form data lives in a ref so typing does not cause parent re-renders. Fields use local state
// initialized once from this ref at mount, and write back to the ref on change.
type FormDataContextValue = {
  formDataRef: React.MutableRefObject<Record<string, any>>;
  setFieldValue: (name: string, value: any) => void;
  /** Set multiple fields at once and bump formDataVersion once (e.g. prefill category + subcategory from payee without clearing subcategory) */
  setMultipleFieldValues: (updates: Record<string, any>) => void;
  clearFieldError: (name: string) => void;
  formLayoutVariant?: FormLayoutVariant;
  /** When this changes (e.g. edit data loaded), fields sync their local state from formDataRef */
  formKey: string;
  /** Bumps when a field that other fields depend on (e.g. category) changes, so dynamic-option fields refetch */
  formDataVersion: number;
};
const FormDataContext = createContext<FormDataContextValue | null>(null);

// Field that owns its value in local state (initialized from ref at mount). Only this field
// re-renders on keystroke; parent and sibling fields do not. Memoized so section edit toggles
// only re-render fields in that section (via disabledOverride prop).
interface FormFieldWithLocalStateProps {
  fieldConfig: FieldConfig;
  error: string | undefined;
  onBlur: (name: string, value: any) => void;
  config: any;
  /** When true, field is disabled (e.g. section not in edit mode). Passed from parent to avoid context-driven re-renders. */
  disabledOverride?: boolean;
  isEdit: boolean;
}
const FormFieldWithLocalState = React.memo(function FormFieldWithLocalState({ fieldConfig, error, onBlur, config, disabledOverride, isEdit }: FormFieldWithLocalStateProps) {
  const ctx = useContext(FormDataContext);
  if (!ctx) throw new Error('FormFieldWithLocalState must be used inside FormDataContext');
  const { formDataRef, setFieldValue, setMultipleFieldValues, clearFieldError, formLayoutVariant, formKey, formDataVersion } = ctx;
  const [value, setValue] = useState(() => formDataRef.current[fieldConfig.name]);
  // Sync from formDataRef when edit data loads (formKey) or when a dependency changes (formDataVersion, e.g. client → invoice cleared).
  // useLayoutEffect so dependent fields (e.g. invoice list) see cleared values in the same frame as the new client id.
  useLayoutEffect(() => {
    setValue(formDataRef.current[fieldConfig.name]);
  }, [formKey, formDataVersion, fieldConfig.name, formDataRef]);
  const filterField = config?.filters?.fields?.find((f: any) => f.key === fieldConfig.name);
  // Compute during render so we always pass latest fieldConfig (including options) — avoid useMemo stale options
  const mergedFieldConfig: FieldConfig = {
    ...fieldConfig,
    apiEndpoint: fieldConfig.apiEndpoint || filterField?.apiEndpoint,
    labelField: fieldConfig.labelField || filterField?.labelField,
    disabled: fieldConfig.disabled || disabledOverride,
  };
  const getFormData = useCallback(() => formDataRef.current, [formDataRef]);
  const handleChange = useCallback((name: string, newValue: any) => {
    setValue(newValue);
    setFieldValue(name, newValue);
    clearFieldError(name);
    const sideEffect = fieldConfig.onChangeSideEffect;
    const runOnClose = fieldConfig.runSideEffectOnClose;
    if (sideEffect && !runOnClose) {
      Promise.resolve(sideEffect(newValue, setFieldValue, setMultipleFieldValues, getFormData)).catch(() => {});
    }
  }, [setFieldValue, setMultipleFieldValues, clearFieldError, fieldConfig.onChangeSideEffect, fieldConfig.runSideEffectOnClose, getFormData]);
  const onSelectClose = useCallback(
    (closeValue: any) => {
      const sideEffect = fieldConfig.onChangeSideEffect;
      if (fieldConfig.runSideEffectOnClose && sideEffect) {
        Promise.resolve(sideEffect(closeValue, setFieldValue, setMultipleFieldValues, getFormData)).catch(() => {});
      }
    },
    [fieldConfig.runSideEffectOnClose, fieldConfig.onChangeSideEffect, setFieldValue, setMultipleFieldValues, getFormData]
  );
  return renderField({
    field: mergedFieldConfig,
    value,
    onChange: handleChange,
    setFieldValue,
    setMultipleFieldValues,
    onBlur: (name: string, val: any) => onBlur(name, val),
    onSelectClose: fieldConfig.runSideEffectOnClose ? onSelectClose : undefined,
    error,
    getFormData,
    formLayoutVariant,
    formKey,
    formDataVersion,
    isEdit,
  });
});

interface GenericFormPageProps<T> {
  // Data hook that returns { data/client, isLoading, mutate }
  useData: (id: number) => {
    data?: T | undefined;
    client?: T | undefined;
    isLoading: boolean;
    mutate: () => void | any;
  };
  // Module configuration
  config: {
    module: {
      route: {
        list: string;
        new: string;
        detail: (id: number) => string;
        edit: (id: number) => string;
        /** Optional pipeline/workspace route (e.g. supply case Case View after create). */
        caseView?: (id: number) => string;
      };
    };
    form: {
      new: {
        title: string;
        subtitle?: string;
        submitText: string;
      };
      edit: {
        title: string;
        subtitle?: string;
        submitText: string;
      };
      cancelText: string;
      loadingText: string;
      errorMessage: string;
      supportsFiles?: boolean;
      /** When set, edit form can open existing attachments (GET blob) in a new tab. `entityId` is the record id from the route. */
      getViewFileUrl?: (entityId: number, fileId: number) => string;
      create: (data: Partial<T>, files?: File[], filesToDetach?: number[]) => Promise<T>;
      update: (id: number, data: Partial<T>, files?: File[], filesToDetach?: number[]) => Promise<T>;
      /** When true for current item (edit mode), all form fields are read-only */
      disableWhen?: (item: T) => boolean;
      /** Shown when form is read-only due to disableWhen (e.g. "Offer already accepted") */
      disabledMessage?: string | ((item: T) => string);
      /** After successful create, navigate to `module.route.caseView(id)` instead of list/back. */
      redirectToCaseViewAfterCreate?: boolean;
      /** After successful create, navigate to edit URL instead of list/back. */
      redirectToEditAfterCreate?: boolean;
      /** 'content' = form height fits fields; 'fill' (default) = stretch to viewport */
      formSizing?: 'content' | 'fill';
      /** Tailwind max-width class for the form container (e.g. 'max-w-3xl') */
      formMaxWidth?: string;
      /** Once when opening the new-record form (after initial data is seeded), e.g. async ref preview. */
      prefillNewRecord?: (helpers: {
        setFieldValue: (name: string, value: any) => void;
        setMultipleFieldValues: (updates: Record<string, any>) => void;
        getFormData: () => Record<string, any>;
      }) => void | Promise<void>;
    };
    fields: FieldConfig[];
    formLayout: {
      mode?: 'default' | 'tabs';
      sections: Array<{
        id: string;
        title: string;
        icon?: string | React.ReactNode;
        fields: string[];
        columns?: number;
        collapsible?: boolean;
        fieldSpans?: Record<string, string>;
        /** When set, section is shown only if predicate is true (e.g. posting-account-only fields). */
        visibleWhen?: (formData: Record<string, unknown>) => boolean;
      }>;
    };
    getInitialFormData: () => Partial<T>;
    mapItemToFormData: (item: T) => Partial<T>;
    /** When provided, rendered below the main layout. Use for e.g. product photos: on new show a hint, on edit show upload+gallery. */
    renderEditFormExtra?: (props: { isEdit: boolean; data?: T; id?: number }) => React.ReactNode;
  };
  /** When provided, these options override config field options so selects always get live data (e.g. clientOptions). */
  fieldOptions?: Record<string, Array<{ value: string | number; label: string }>>;
  /** When true for a field name, that input is disabled (e.g. reference locked by template policy). */
  fieldDisabled?: Record<string, boolean>;
  /** When true for a field name, that input is read-only (still shows value clearly; use instead of disabled for locked refs). */
  fieldReadOnly?: Record<string, boolean>;
}

function GenericFormPage<T extends { [key: string]: any }>({
  useData,
  config,
  fieldOptions,
  fieldDisabled,
  fieldReadOnly,
}: GenericFormPageProps<T>) {
  const { id: idParam } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { mutate: globalMutate } = useSWRConfig();
  // Only treat as edit when id is a valid numeric string (avoids "new" or invalid id → NaN)
  const editId = idParam && idParam !== 'new' ? parseInt(idParam, 10) : NaN;
  const isEdit = Number.isInteger(editId);
  const id = isEdit ? String(editId) : idParam;
  const isMobile = false;
  const dataHookResult = useData(isEdit ? editId : 0);
  const data = (dataHookResult.data || dataHookResult.client) as T | undefined;
  const loadingData = dataHookResult.isLoading;
  const { colors } = useTheme();
  const { showSuccess, showError } = useNotification();
  const formLayoutVariant = 'default' as FormLayoutVariant;

  const buildInitialFormData = useCallback((): Partial<T> => {
    const initialData = config.getInitialFormData();
    // Pre-fill form data from URL search params (e.g., ?clientId=123)
    if (!isEdit) {
      searchParams.forEach((value, key) => {
        // Try to parse as number if it's a numeric string
        const numValue = /^\d+$/.test(value) ? parseInt(value, 10) : value;
        (initialData as any)[key] = numValue;
      });
    }
    return initialData;
  }, [config, isEdit, searchParams]);

  const formDataRef = useRef<Record<string, any>>(buildInitialFormData() as Record<string, any>);
  const formKeyRef = useRef<string>('');
  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const formConfigTyped = config.form as {
    supportsFiles?: boolean;
    getViewFileUrl?: (entityId: number, fileId: number) => string;
    attachmentsLabel?: string;
    attachmentsHint?: string;
    fileAccept?: string;
  };
  const supportsFiles = !!formConfigTyped.supportsFiles;
  const getViewFileUrlForEdit =
    isEdit && Number.isInteger(editId) && formConfigTyped.getViewFileUrl
      ? (fileId: number) => formConfigTyped.getViewFileUrl!(editId, fileId)
      : undefined;
  const [files, setFiles] = useState<File[]>([]);
  const [filesToDetach, setFilesToDetach] = useState<number[]>([]);
  const existingFiles = (data as any)?.FileAttachment ?? [];
  const visibleExistingFiles = useMemo(
    () => existingFiles.filter((f: { id: number }) => !filesToDetach.includes(f.id)),
    [existingFiles, filesToDetach]
  );

  const formKey = isEdit ? `edit-${id}-${data?.id ?? 'loading'}` : `new-${location.key}`;
  if (formKey !== formKeyRef.current) {
    formKeyRef.current = formKey;
    formDataRef.current = (isEdit && data ? config.mapItemToFormData(data) : buildInitialFormData()) as Record<string, any>;
  }

  const { dependencyFieldNames, dependentFieldsByDep } = useMemo(() => {
    const depSet = new Set<string>();
    const depToFields: Record<string, string[]> = {};
    config.fields.forEach((f: { name: string; optionsDependOnFields?: string[] }) => {
      (f.optionsDependOnFields || []).forEach((dep: string) => {
        depSet.add(dep);
        if (!depToFields[dep]) depToFields[dep] = [];
        depToFields[dep].push(f.name);
      });
    });
    return { dependencyFieldNames: depSet, dependentFieldsByDep: depToFields };
  }, [config.fields]);

  const [formDataVersion, setFormDataVersion] = useState(0);

  const fieldToSectionMap = useMemo(() => {
    const map: Record<string, string> = {};
    config.formLayout?.sections?.forEach((section) => {
      section.fields.forEach((fieldName) => {
        map[fieldName] = section.id;
      });
    });
    return map;
  }, [config.formLayout]);

  const initialSectionEditState = useMemo(() => {
    const state: Record<string, boolean> = {};
    config.formLayout?.sections?.forEach((section) => {
      if ((section as { toggleable?: boolean; defaultDisabled?: boolean }).toggleable) {
        state[section.id] = (section as { defaultDisabled?: boolean }).defaultDisabled !== true;
      }
    });
    return state;
  }, [config.formLayout]);
  const [sectionEditState, setSectionEditState] = useState<Record<string, boolean>>(initialSectionEditState);
  const onSectionEditToggle = useCallback((sectionId: string, enabled: boolean) => {
    setSectionEditState((prev) => ({ ...prev, [sectionId]: enabled }));
  }, []);

  const setFieldValue = useCallback((name: string, value: any) => {
    formDataRef.current = { ...formDataRef.current, [name]: value };
    if (dependencyFieldNames.has(name)) {
      const dependentNames = dependentFieldsByDep[name] || [];
      dependentNames.forEach((fieldName: string) => {
        const field = config.fields.find((f: { name: string; type?: string }) => f.name === fieldName);
        const emptyValue = field?.type === 'multiselect' ? [] : '';
        formDataRef.current = { ...formDataRef.current, [fieldName]: emptyValue };
      });
      setFormDataVersion((v) => v + 1);
    }
    // Re-render layout so section visibleWhen (e.g. IMAP vs POP3, COA isPosting, vessel fields) sees fresh formDataRef — refs alone do not re-render parents.
    if (name === 'incomingProtocol' || name === 'isPosting' || name === 'isVessel') {
      setFormDataVersion((v) => v + 1);
    }
  }, [dependencyFieldNames, dependentFieldsByDep, config.fields]);

  const setMultipleFieldValues = useCallback((updates: Record<string, any>) => {
    formDataRef.current = { ...formDataRef.current, ...updates };
    // Defer version bump so the current interaction (e.g. select closing) completes and UI stays responsive
    setTimeout(() => setFormDataVersion((v) => v + 1), 0);
  }, []);

  const handleFileChange = useCallback((newFiles: File[]) => {
    setFiles(newFiles);
  }, []);

  const handleRemoveExistingFile = useCallback((fileId: number) => {
    setFilesToDetach((prev) => [...prev, fileId]);
  }, []);

  const goBack = useNavigateBack(config.module.route.list, getLocationReturnPath(location.state));

  useEffect(() => {
    if (isEdit && Number.isInteger(editId)) {
      const moduleApi = (config.module as { api?: { detail?: (id: number) => string } }).api;
      const detailKey = moduleApi?.detail?.(editId);
      if (detailKey) globalMutate(detailKey);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEdit, editId]);

  const prefillNewRecordRanForFormKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (isEdit) return;
    const fn = config.form.prefillNewRecord;
    if (!fn) return;
    if (prefillNewRecordRanForFormKeyRef.current === formKey) return;
    prefillNewRecordRanForFormKeyRef.current = formKey;
    void Promise.resolve(
      fn({
        setFieldValue,
        setMultipleFieldValues,
        getFormData: () => ({ ...formDataRef.current }),
      }),
    ).catch(() => {});
  }, [isEdit, formKey, config.form, setFieldValue, setMultipleFieldValues]);

  const clearFieldError = useCallback((name: string) => {
    setFormErrors((prev) => {
      if (!prev[name]) return prev;
      const next = { ...prev };
      delete next[name];
      return next;
    });
  }, []);

  const formDataContextValue = useMemo<FormDataContextValue>(() => ({
    formDataRef,
    setFieldValue,
    setMultipleFieldValues,
    clearFieldError,
    formLayoutVariant,
    formKey,
    formDataVersion,
  }), [setFieldValue, setMultipleFieldValues, clearFieldError, formLayoutVariant, formKey, formDataVersion]);

  const handleBlur = useCallback((name: string, value: any) => {
    const field = config.fields.find((f) => f.name === name);
    if (field && field.validation) {
      const error = validateField(field, value, formDataRef.current as Partial<T>);
      if (error) {
        setFormErrors((prev) => ({
          ...prev,
          [name]: error,
        }));
      } else {
        setFormErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[name];
          return newErrors;
        });
      }
    }
  }, [config.fields]);

  const validateField = (field: FieldConfig, value: any, formDataForValidation?: Partial<T>): string | undefined => {
    if (!field.validation) return undefined;

    const { validation } = field;
    const isRequired =
      validation.required === true ||
      (typeof validation.requiredWhen === 'function' && formDataForValidation && validation.requiredWhen(formDataForValidation));

    // Map field type to validation type
    let validationType: 'email' | 'phone' | 'number' | 'date' | 'url' | undefined;
    if (field.type === 'email') validationType = 'email';
    else if (field.type === 'tel' || field.type === 'phone') validationType = 'phone';
    else if (field.type === 'number') validationType = 'number';
    else if (field.type === 'date' || field.type === 'datetime') validationType = 'date';
    else if (field.type === 'url') validationType = 'url';

    // Use the validation utility
    const result = validateFieldUtil(value, {
      required: isRequired,
      type: validationType,
      min: validation.min !== undefined ? validation.min : validation.minLength,
      max: validation.max !== undefined ? validation.max : validation.maxLength,
      pattern: validation.pattern,
      patternMessage: validation.message,
    });

    if (!result.valid) {
      return validation.message || result.message || `${field.label} is invalid`;
    }

    return undefined;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const currentData = formDataRef.current as Partial<T>;
    const errors: Record<string, string> = {};
    config.fields.forEach((field) => {
      const fieldError = validateField(field, currentData[field.name as keyof T], currentData);
      if (fieldError) {
        errors[field.name] = fieldError;
      }
    });

    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }

    setIsSubmitting(true);

    try {
      let createdEntityId: number | null = null;
      
      if (isEdit && Number.isInteger(editId)) {
        await config.form.update(
          editId,
          currentData,
          supportsFiles && files.length > 0 ? files : undefined,
          supportsFiles && filesToDetach.length > 0 ? filesToDetach : undefined
        );
        showSuccess(`${config.module.route.list.includes('clients') ? 'Client' : 'Item'} updated successfully`);
        createdEntityId = editId;
      } else {
        const result = await config.form.create(
          currentData,
          supportsFiles && files.length > 0 ? files : undefined,
          supportsFiles && filesToDetach.length > 0 ? filesToDetach : undefined
        );
        showSuccess(`${config.module.route.list.includes('clients') ? 'Client' : 'Item'} created successfully`);
        // Get the ID from the created entity
        createdEntityId = (result as any)?.id || 
                         (result as any)?.clientId || 
                         (result as any)?.taskId || 
                         (result as any)?.ticketId || 
                         (result as any)?.leadId || 
                         (result as any)?.todoId || 
                         (result as any)?.expenseId || 
                         (result as any)?.clientAppId || 
                         null;
      }

      // Save custom fields if entity was created/updated
      if (createdEntityId) {
        const entityType = getEntityTypeFromRoute(config.module.route.list) || 
                          getEntityTypeFromTitle((config.module as any).title || '');
        if (entityType) {
          // Extract custom field values from formData
          const customFieldEntries = Object.entries(currentData).filter(([key]) => 
            key.startsWith('_customFields_')
          );
          
          // Save each custom field value
          for (const [key, value] of customFieldEntries) {
            const fieldId = parseInt(key.replace('_customFields_', ''));
            if (!isNaN(fieldId) && value !== undefined && value !== null) {
              try {
                await setCustomFieldValue(entityType, createdEntityId, fieldId, value);
              } catch (err: any) {
                console.error(`Failed to save custom field ${fieldId}:`, err);
                // Don't fail the whole form submission if custom field save fails
              }
            }
          }
        }
      }

      // Revalidate list and detail SWR cache so list/detail views show fresh data after add/edit
      const listApiBase = (config.module as { api?: { list: string } }).api?.list;
      if (listApiBase) {
        globalMutate((key: string) => typeof key === 'string' && key.startsWith(listApiBase));
      }
      if (dataHookResult.mutate) {
        dataHookResult.mutate();
      }

      const redirectToCaseView = config.form.redirectToCaseViewAfterCreate === true;
      const caseViewRoute = config.module.route.caseView;
      const redirectToEdit = config.form.redirectToEditAfterCreate === true;
      const returnFrom =
        getLocationReturnPath(location.state) ?? config.module.route.list;
      if (!isEdit && createdEntityId && redirectToCaseView && typeof caseViewRoute === 'function') {
        navigate(caseViewRoute(createdEntityId), { state: { from: returnFrom } });
      } else if (!isEdit && createdEntityId && redirectToEdit) {
        navigate(config.module.route.edit(createdEntityId), { state: { from: returnFrom } });
      } else {
        goBack();
      }
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || err.message || config.form.errorMessage;
      setError(errorMessage);
      showError(errorMessage);
      console.error('Error saving:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    goBack();
  };

  const isFormReadOnlyByConfig = !!(config.form.disableWhen && data && config.form.disableWhen(data));

  // Build field tree on every render. When fieldOptions is provided, use it so selects get live options (e.g. from useClientsForSelect).
  // disabledOverride per field so only fields in toggleable sections re-render when section edit state changes (FormFieldWithLocalState is memoized).
  const renderedFields: Record<string, React.ReactNode> = {};
  config.fields.forEach((fieldConfig) => {
    const liveOptions = fieldOptions?.[fieldConfig.name];
    const fieldConfigWithOptions =
      liveOptions && Array.isArray(liveOptions)
        ? { ...fieldConfig, options: liveOptions }
        : fieldConfig;
    const fieldDisabledByPolicy = fieldDisabled?.[fieldConfig.name] === true;
    const fieldReadOnlyByPolicy = fieldReadOnly?.[fieldConfig.name] === true;
    const fieldConfigWithPolicy = {
      ...fieldConfigWithOptions,
      ...(fieldReadOnlyByPolicy ? { readOnly: true } : {}),
      ...(fieldDisabledByPolicy ? { disabled: true } : {}),
    };
    const sectionId = fieldToSectionMap[fieldConfig.name];
    const formDisabledBySection = sectionId != null && sectionEditState[sectionId] === false;
    const disabledOverride = formDisabledBySection || isFormReadOnlyByConfig;
    renderedFields[fieldConfig.name] = (
      <FormFieldWithLocalState
        key={fieldConfig.name}
        fieldConfig={fieldConfigWithPolicy}
        error={formErrors[fieldConfig.name]}
        onBlur={handleBlur}
        config={config}
        disabledOverride={disabledOverride}
        isEdit={isEdit}
      />
    );
  });

  const formConfig = isEdit ? config.form.edit : config.form.new;
  const formSizing = config.form.formSizing ?? 'fill';
  const formMaxWidth = config.form.formMaxWidth ?? 'max-w-[67.2rem]';

  if (isEdit && loadingData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <LoadingSpinner size="large" message={config.form.loadingText} />
      </div>
    );
  }

  return (
    <div className={`h-full flex flex-col ${formSizing === 'content' ? 'min-h-0' : ''}`}>
      <FormBackButton onClick={handleCancel} className={isMobile ? 'mb-2' : 'mb-3'} />
      <div
        className={`${isMobile ? 'w-full px-2' : `${formMaxWidth} mx-auto w-full`} flex flex-col ${formSizing === 'fill' ? 'h-full' : ''}`}
      >
        {/* Error Message - Fixed */}
        {error && (
          <div className={`flex-shrink-0 ${isMobile ? 'mb-3' : 'mb-4'} bg-red-50 border border-red-200 rounded-lg ${isMobile ? 'px-3 py-2' : 'px-4 py-2.5'}`}>
            <p className={`text-sm text-red-800 ${isMobile ? 'text-xs' : ''}`}>{error}</p>
          </div>
        )}

        {/* Read-only notice when form is disabled by config (e.g. offer already accepted/declined) */}
        {isEdit && data && isFormReadOnlyByConfig && config.form.disabledMessage && (
          <div className={`flex-shrink-0 ${isMobile ? 'mb-3' : 'mb-4'} bg-amber-50 border border-amber-200 rounded-lg ${isMobile ? 'px-3 py-2' : 'px-4 py-2.5'}`}>
            <p className={`text-sm text-amber-800 ${isMobile ? 'text-xs' : ''}`}>
              {typeof config.form.disabledMessage === 'function' ? config.form.disabledMessage(data) : config.form.disabledMessage}
            </p>
          </div>
        )}

        {/* Form - Scrollable */}
        <form
          key={formKey}
          onSubmit={handleSubmit}
          className={`${formSizing === 'fill' ? 'flex-1' : ''} flex flex-col ${colors.surface} rounded-lg shadow-sm border ${colors.border} overflow-hidden ${(formLayoutVariant === 'compact' || formLayoutVariant === 'minimal') ? 'rounded-md' : ''}`}
        >
          <FormDataContext.Provider value={formDataContextValue}>
          {/* Scrollable Form Content */}
          <div className={`${formSizing === 'fill' ? 'flex-1 overflow-y-auto dashboard-scrollbar' : ''} ${(formLayoutVariant === 'compact' || formLayoutVariant === 'minimal') ? 'px-3 pt-3 pb-3' : isMobile ? 'px-3 pt-3 pb-3' : 'px-5 pt-4 pb-4'}`}>
            <FormLayoutRenderer
              layout={config.formLayout}
              fields={renderedFields}
              formData={formDataRef.current}
              isMobile={isMobile}
              variant={formLayoutVariant}
              sectionEditState={sectionEditState}
              onSectionEditToggle={onSectionEditToggle}
            />

            {/* Form extra content (e.g. catalog item product photos: message on new, upload+gallery on edit) */}
            {(config as { renderEditFormExtra?: (props: { isEdit: boolean; data?: T; id?: number }) => React.ReactNode }).renderEditFormExtra && (
              <div className={(formLayoutVariant === 'compact' || formLayoutVariant === 'minimal') ? `mt-3 pt-3 border-t ${colors.border}` : `mt-6 pt-6 border-t ${colors.border}`}>
                {(config as { renderEditFormExtra: (props: { isEdit: boolean; data?: T; id?: number }) => React.ReactNode }).renderEditFormExtra({
                  isEdit: !!isEdit,
                  data: data,
                  id: Number.isInteger(editId) ? editId : undefined,
                })}
              </div>
            )}

            {/* Custom Fields Section */}
            {(() => {
              const entityType = getEntityTypeFromRoute(config.module.route.list) || 
                                 getEntityTypeFromTitle((config.module as any).title || '');
              const entityId = isEdit && Number.isInteger(editId) ? editId : null;
              const customFieldValuesForNew = !entityId ? (() => {
                const out: Record<number, any> = {};
                Object.entries(formDataRef.current).forEach(([key, value]) => {
                  if (key.startsWith('_customFields_')) {
                    const fieldId = parseInt(key.replace('_customFields_', ''), 10);
                    if (!isNaN(fieldId) && value !== undefined) out[fieldId] = value;
                  }
                });
                return out;
              })() : undefined;
              if (entityType) {
                return (
                  <div className={(formLayoutVariant === 'compact' || formLayoutVariant === 'minimal') ? `mt-3 pt-3 border-t ${colors.border}` : `mt-4 pt-4 border-t ${colors.border}`}>
                    <h3 className={(formLayoutVariant === 'compact' || formLayoutVariant === 'minimal') ? `text-sm font-semibold ${colors.text} mb-2` : `text-base font-semibold ${colors.text} mb-3`}>Custom Fields</h3>
                    <DynamicFieldRenderer
                      entityType={entityType}
                      entityId={entityId}
                      valuesForNewEntity={customFieldValuesForNew}
                      onValueChange={(fieldId, value) => {
                        if (!entityId) setFieldValue(`_customFields_${fieldId}`, value);
                      }}
                    />
                  </div>
                );
              }
              return null;
            })()}

            {/* File attachments section (when form supports files) */}
            {supportsFiles && (
              <div className={(formLayoutVariant === 'compact' || formLayoutVariant === 'minimal') ? `mt-3 pt-3 border-t ${colors.border}` : `mt-6 pt-6 border-t ${colors.border}`}>
                <FileAttachmentsFormBlock
                  label={formConfigTyped.attachmentsLabel || 'Photos & files'}
                  hint={
                    formConfigTyped.attachmentsHint ||
                    'Upload item photos and documents (PDF, Word, Excel, images). Applied when you save.'
                  }
                  accept={formConfigTyped.fileAccept || '*/*'}
                  newFiles={files}
                  existingFiles={visibleExistingFiles.map((f: { id: number; fileName?: string; fileSize?: number }) => ({
                    id: f.id,
                    fileName: f.fileName,
                    fileSize: f.fileSize,
                  }))}
                  onNewFilesChange={handleFileChange}
                  onRemoveExisting={handleRemoveExistingFile}
                  getViewFileUrl={getViewFileUrlForEdit}
                />
              </div>
            )}
          </div>

          {/* Form Actions - Fixed at bottom, sticky on mobile */}
          <div
            className={`flex-shrink-0 flex ${isMobile ? 'flex-col-reverse gap-2' : 'items-center justify-end space-x-3'} ${(formLayoutVariant === 'compact' || formLayoutVariant === 'minimal') ? 'p-3' : isMobile ? 'p-4' : 'p-6'} border-t ${colors.border} ${colors.surfaceHover} ${isMobile ? 'sticky bottom-0 z-10 shadow-lg' : ''}`}
          >
            <Button 
              type="button" 
              onClick={handleCancel} 
              variant="outline"
              className={isMobile ? 'w-full min-h-[44px]' : ''}
            >
              {config.form.cancelText}
            </Button>
            <Button 
              type="submit" 
              disabled={isSubmitting || isFormReadOnlyByConfig} 
              loading={isSubmitting}
              className={isMobile ? 'w-full min-h-[44px]' : ''}
            >
              {formConfig.submitText}
            </Button>
          </div>
          </FormDataContext.Provider>
        </form>
      </div>
    </div>
  );
}

export default GenericFormPage;

